import { defineStore } from 'pinia'
import type {
  LoginRequest,
  AccessTokenResponse,
  CurrentUser,
  CreateSignalSessionResponse,
} from '@/types/auth'
import axios from '@/lib/axios'
import { isAxiosError } from 'axios'
import {
  clearAuth,
  saveAccessToken,
  isAccessTokenExpired,
  tryGetAccessToken,
} from '@/utils/authUtils'
import { useStorage } from '@vueuse/core'
import { useLocalitiesStore } from '@/stores/localities'

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null as CurrentUser | null,
    token: tryGetAccessToken() as string | null,
    isDarkMode: useStorage('darkMode', true),
  }),
  getters: {
    isAuthenticated(): boolean {
      return !!this.token && !!this.user && !isAccessTokenExpired()
    },
  },
  actions: {
    async login(credentials: LoginRequest) {
      try {
        const response = await axios.post<AccessTokenResponse>(
          '/login?useCookies=false',
          credentials,
        )

        const { accessToken: newToken, refreshToken } = response.data

        // Store tokens
        saveAccessToken(newToken, refreshToken, response.data.expiresIn)
        this.token = newToken // Update reactive token state

        await this.fetchUser()
        return response.data
      } catch (error: unknown) {
        if (isAxiosError(error) && error.response?.status === 401) {
          throw new Error('Invalid credentials')
        }
        throw error
      }
    },
    async logout() {
      clearAuth()
      this.token = null // Clear reactive token state
      this.user = null // Clear reactive user state
    },
    async fetchUser() {
      try {
        const response = await axios.get<CurrentUser>('/users/me')
        this.user = response.data
        const localitiesStore = useLocalitiesStore()

        // Default selected locality to the user's assigned locality on login/init.
        if (this.user?.locality) {
          localitiesStore.setSelectedLocality(this.user.locality)
        }

        // Fetch available localities for Global Admins
        if (this.user?.isGlobalAdmin) {
          await localitiesStore.fetchLocalities()
        }
      } catch (error) {
        console.error('Error fetching user:', error)
        if (isAxiosError(error) && error.response?.status === 401) {
          clearAuth()
          this.token = null // Clear reactive token state
          this.user = null // Clear reactive user state
        }
      }
    },
    async init(): Promise<boolean> {
      // If we already have a token and user, we don't need to fetch them again
      // as axios is listening for any failed requests and will clear the token and user if it receives a 401 or 403.
      if (this.token && this.user) {
        console.debug('Already have a token and user, skipping  remainder of init')
        return true
      }

      console.debug('Initializing auth store')

      const currentToken = tryGetAccessToken()
      if (currentToken) {
        console.debug('Setting reactive token state')
        this.token = currentToken // Set reactive token state
        try {
          console.debug('Fetching user')
          await this.fetchUser()
          console.debug('Auth store initialized')
          return true
        } catch (error) {
          console.error('Error fetching user:', error)
          clearAuth()
          this.token = null // Clear reactive token state
          this.user = null // Clear reactive user state
        }
      } else {
        console.debug('No token found, skipping fetchUser')
      }
      return false
    },

    toggleDarkMode() {
      this.isDarkMode = !this.isDarkMode
      document.documentElement.classList.toggle('dark', this.isDarkMode)
    },
    initializeDarkMode() {
      document.documentElement.classList.toggle('dark', this.isDarkMode)
    },
    async updateProfile(data: CurrentUser & { preferredMeetingType1Id?: string | null; preferredMeetingType2Id?: string | null }) {
      try {
        const response = await axios.put<CurrentUser>('/users/me', data)
        this.user = response.data
        return response.data
      } catch (error) {
        if (isAxiosError(error)) {
          throw new Error(error.response?.data?.message || 'Failed to update profile')
        }
        throw error
      }
    },
    async getSignalSessionLinkQr(): Promise<CreateSignalSessionResponse> {
      const response = await axios.get('/signal/initiate-session')
      // response is a qr code image in the base64 format
      return await response.data
    },
    async checkSignalSessionStatus(): Promise<boolean> {
      const response = await axios.get('/signal/get-session-status')
      return response.data.IsLinked === true
    },
  },
})
